import { formatDate } from '@angular/common'
import { Persona } from '@app/v2/personas/types/persona.type'
import { EventStatus } from '../enums/event.enum'
import { SourceEventOption, SourcePersonaOption } from './add-from-other-event.type'
import { Event } from './event.type'
import { selfPrintingPersonaSettings } from './settings.type'

export class AddFromOtherEventModel {
  static readonly HEADER_PLUS_ICON = `<svg class="tw-shrink-0 tw-block" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M13.2222 6.22222H7.77778V0.777778C7.77778 0.571498 7.69583 0.373667 7.54997 0.227806C7.40411 0.0819442 7.20628 0 7 0C6.79372 0 6.59589 0.0819442 6.45003 0.227806C6.30417 0.373667 6.22222 0.571498 6.22222 0.777778V6.22222H0.777778C0.571498 6.22222 0.373667 6.30417 0.227806 6.45003C0.0819442 6.59589 0 6.79372 0 7C0 7.20628 0.0819442 7.40411 0.227806 7.54997C0.373667 7.69583 0.571498 7.77778 0.777778 7.77778H6.22222V13.2222C6.22222 13.4285 6.30417 13.6263 6.45003 13.7722C6.59589 13.9181 6.79372 14 7 14C7.20628 14 7.40411 13.9181 7.54997 13.7722C7.69583 13.6263 7.77778 13.4285 7.77778 13.2222V7.77778H13.2222C13.4285 7.77778 13.6263 7.69583 13.7722 7.54997C13.9181 7.40411 14 7.20628 14 7C14 6.79372 13.9181 6.59589 13.7722 6.45003C13.6263 6.30417 13.4285 6.22222 13.2222 6.22222Z" fill="#111928"/></svg>`
  static readonly OPTIONS_MAX_HEIGHT_PX = 270
  static readonly SELECT_EVENT_FIRST_ERROR = 'Select an event first'

  static readonly STATUS_LABELS: Partial<Record<keyof typeof EventStatus, string>> = {
    LIVE: 'Live',
    UPCOMING: 'Upcoming',
    FINISHED: 'Finished'
  }

  static readonly NEUTRAL_STATUS_BADGE_CLASS = 'tw-bg-gray-100 tw-text-gray-900'

  static readonly STATUS_BADGE_CLASSES: Partial<Record<keyof typeof EventStatus, string>> = {
    LIVE: 'tw-bg-green-100 tw-text-green-800',
    UPCOMING: 'tw-bg-yellow-100 tw-text-yellow-800',
    FINISHED: AddFromOtherEventModel.NEUTRAL_STATUS_BADGE_CLASS
  }

  /**
   * Maps an event into a source-event dropdown option (status badge + date range).
   */
  static toEventOption(event: Event): SourceEventOption {
    const status = event.status ?? ''
    const statusKey = AddFromOtherEventModel._isStatusKey(status) ? status : null
    return {
      id: event.id,
      name: event.name,
      statusLabel: (statusKey && AddFromOtherEventModel.STATUS_LABELS[statusKey]) || status,
      statusBadgeClass: (statusKey && AddFromOtherEventModel.STATUS_BADGE_CLASSES[statusKey]) || AddFromOtherEventModel.NEUTRAL_STATUS_BADGE_CLASS,
      dateRange: `${AddFromOtherEventModel._formatDate(event.public_start_date)} - ${AddFromOtherEventModel._formatDate(event.public_end_date)}`
    }
  }

  /**
   * Maps persona settings rows into personas with registration counts; rows without an id are dropped.
   */
  static toPersonas(rows: selfPrintingPersonaSettings): Persona[] {
    return rows
      .filter((row): row is selfPrintingPersonaSettings[number] & { id: number } => row.id != null)
      .map((row) => ({
        id: row.id,
        name: row.name ?? '',
        slug: row.slug ?? '',
        registration_count: row.registration_count ?? 0
      }))
  }

  /**
   * Maps a persona into a source-persona dropdown option; personas without registrations are disabled.
   */
  static toPersonaOption(persona: Persona): SourcePersonaOption {
    const registrationCount = persona.registration_count ?? 0
    const disabled = registrationCount === 0
    return {
      id: persona.id,
      name: persona.name.toUpperCase(),
      registrationCount: registrationCount.toLocaleString(),
      disabled,
    }
  }

  private static _isStatusKey(status: string): status is keyof typeof EventStatus {
    return status in EventStatus
  }

  private static _formatDate(value: string): string {
    const datePart = value?.slice(0, 10)
    return datePart ? formatDate(datePart, 'dd MMM yyyy', 'en-US') : ''
  }
}
